import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { translateFarmsError } from '../lib/translateError';

export interface ChatThreadSummary {
  thread_id:        string;
  farm_id:          string | null;
  farm_name:        string | null;
  farm_slug:        string | null;
  farm_owner_id:    string | null;
  other_user_id:    string | null;
  other_user_label: string | null;
  subject:          string | null;
  last_message:     string | null;
  last_message_at:  string | null;
  last_sender_id:   string | null;
  unread_count:     number;
}

/**
 * Título da conversa do ponto de vista do usuário atual:
 * - Sou o dono da farm → mostro quem é o CLIENTE (nome/e-mail).
 * - Sou o cliente → mostro o nome da farm.
 */
export function threadTitle(t: ChatThreadSummary, myUserId: string | undefined): string {
  const iAmFarmOwner = !!myUserId && t.farm_owner_id === myUserId;
  // Outro participante removido (conta excluída) e farm inexistente → desativada
  if (!t.other_user_id && (iAmFarmOwner || !t.farm_name)) return 'Conta desativada';
  if (iAmFarmOwner) return t.other_user_label ?? 'Conta desativada';
  return t.farm_name ?? t.subject ?? t.other_user_label ?? 'Conversa';
}

/**
 * Lista as conversas do usuário logado via RPC `list_chat_threads`
 * (migration 20260704000001) e recarrega em tempo real quando chega
 * notificação de nova mensagem.
 */
export function useChatThreads() {
  const { user } = useAuth();
  const [threads, setThreads] = useState<ChatThreadSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    const { data, error } = await supabase.rpc('list_chat_threads' as never);
    if (error) {
      setError(translateFarmsError(error.message));
      setThreads([]);
    } else {
      setError(null);
      setThreads((data as unknown as ChatThreadSummary[]) ?? []);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void load();
    if (!user) return;

    // Recarrega quando o usuário recebe notificação de nova mensagem.
    // Tópico único por instância: o hook é usado em mais de um componente
    // simultaneamente (ChatFab + página Conversas) e canais Realtime com o
    // mesmo nome não aceitam novos callbacks após subscribe() (tela branca).
    const ch = supabase
      .channel(`chat-inbox:${user.id}:${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        (payload) => {
          if ((payload.new as { type?: string }).type === 'new_message') void load();
        }
      )
      .subscribe();

    return () => { void supabase.removeChannel(ch); };
  }, [user, load]);

  const totalUnread = threads.reduce((acc, t) => acc + Number(t.unread_count ?? 0), 0);

  return { threads, loading, error, totalUnread, reload: load };
}
